import React from 'react';
import { StockSymbol } from '@benzinga/session';
import { SendLinkContext } from '../../WidgetLinking';
import { CustomCellRendererProps } from 'ag-grid-react';
import { TickerColor, TickerColorV2 } from '../../TickerColor';
import styled from '@benzinga/themetron';
import {
  HorizontalRuleModule,
  QuoteData,
  SparkLineModule,
  TickerHeaderModule,
  TickerNotesModule,
} from '../../TickerHover/modules';
import { absColDef, AdvancedColDef } from '../helpers/advancedHeader';
import { TickerPopover } from '../../TickerHover/popover';
import { TickerHover } from '../../TickerHover/hover';

interface NoteCellData {
  symbol: StockSymbol;
}

interface OwnProps {
  data: NoteCellData;
  isOnlySymbol?: boolean;
}

type Props = OwnProps & CustomCellRendererProps;

const SymbolRender: React.FC<Props> = params => {
  const sendLink = React.useContext(SendLinkContext);
  const value = params.value ?? params.data?.ticker ?? params.data?.symbol;
  const isArray = Array.isArray(value);
  const symbol = isArray ? value[0] : value;
  const extraTickers = isArray ? value.length - 1 : 0;

  if (isArray ? value.length === 0 : !value) {
    return null;
  }

  return (
    <TickerPopover
      modules={
        <>
          <TickerHeaderModule onSymbolClick={sendLink.onSymbolClick} />
          <HorizontalRuleModule />
          <SparkLineModule />
          <HorizontalRuleModule />
          <QuoteData symbol={symbol} />
          <HorizontalRuleModule />
          <TickerNotesModule />
        </>
      }
      symbol={symbol}
    >
      <MultipleTickerWrapper>
        {symbol && <TickerColor onSymbolClick={sendLink.onSymbolClick} symbol={symbol} />}
        {extraTickers > 0 && <MoreCountSpan> +{extraTickers}</MoreCountSpan>}
      </MultipleTickerWrapper>
    </TickerPopover>
  );
};

const SymbolRenderV2: React.FC<Props> = params => {
  const sendLink = React.useContext(SendLinkContext);
  const isArray = Array.isArray(params.value);

  if (isArray ? params.value.length === 0 : !params.value) {
    return null;
  }

  const symbol = isArray ? params.value[0] : params.value;
  const extraTickers = isArray ? params.value.length - 1 : 0;

  return (
    <TickerPopover
      modules={
        <>
          <TickerHeaderModule onSymbolClick={sendLink.onSymbolClick} />
          <HorizontalRuleModule />
          <SparkLineModule />
          <HorizontalRuleModule />
          <TickerNotesModule />
        </>
      }
      symbol={symbol}
    >
      <MultipleTickerWrapper>
        {symbol && <TickerColorV2 symbol={symbol} />}
        {extraTickers > 0 && <MoreCountSpan> +{extraTickers}</MoreCountSpan>}
      </MultipleTickerWrapper>
    </TickerPopover>
  );
};

const TooltipRender = (params: CustomCellRendererProps) => {
  const sendLink = React.useContext(SendLinkContext);
  const isArray = Array.isArray(params.value);

  if ((isArray && params.value.length !== 1) || !params.value) {
    return null;
  }
  const symbol = isArray ? params.value[0] : params.value;
  return (
    <TickerHover
      modules={
        <>
          <TickerHeaderModule onSymbolClick={sendLink.onSymbolClick} />
          <HorizontalRuleModule />
          <SparkLineModule />
          <HorizontalRuleModule />
          <TickerNotesModule />
        </>
      }
      symbol={symbol}
    />
  );
};

export const SymbolColDef = (colDef: AdvancedColDef): AdvancedColDef => ({
  cellClass: ['u-alignLeft', 'u-cursor--pointer'],
  cellRenderer: SymbolRender,
  colId: 'symbol',
  filter: 'agSetColumnFilter',
  initialPinned: true,
  // tooltipComponent: TooltipRender,
  // tooltipField: colDef.field,
  // tooltipValueGetter: typeof colDef.valueGetter === 'function' ? (colDef.valueGetter as any) : undefined,
  ...colDef,
  ...absColDef(colDef),
});

export const SymbolColDefV2 = (colDef: AdvancedColDef): AdvancedColDef => ({
  cellClass: ['u-alignLeft', 'u-cursor--pointer'],
  cellRenderer: SymbolRenderV2,
  filter: 'agSetColumnFilter',
  initialPinned: true,
  ...colDef,
  ...absColDef(colDef),
});

const MultipleTickerWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const MoreCountSpan = styled.span`
  font-size: 0.75em;
  margin-left: 0.25em;
  vertical-align: top;
`;
